import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiConfigs } from "../../constants/apiConfigs";
import { CollaborationData } from "../../types/collaborations/CollaborationTypes";
import { isFresh } from "../../utils/helpers";
import { ERROR, errors } from "../../constants/errors";
import { fetchCollaboration as fetchCollaborationAPI } from "../../services/api/collaboration";
import { CollaborationChatRoom } from "../../types/collaborations/chat";
import { getCollabChatRoom } from "../../services/api/chat";

export interface CollaborationDetailsSlice {
    loading: boolean,
    collaborations: Record<string, { collaborationData: CollaborationData, last_fetched_at: number, expired: boolean }>,
    chatRooms: Record<string, {room: CollaborationChatRoom, last_fetched_at: number}>,
    error: string | null,
    error_code: number | null
}

export const initialState: CollaborationDetailsSlice = {
    loading: false,
    collaborations: {},
    chatRooms: {},
    error: null,
    error_code: null,
}

const STALE_TIME = apiConfigs.FETCH_COLLABORATION.staleTime;

export const fetchCollaboration = createAsyncThunk(
    'collaborations/fetchCollaboration',
    async ({campaignId, force=false}: {campaignId: string, force?: boolean}, { getState, rejectWithValue }) => {
        const state = getState() as { collaborationDetails: CollaborationDetailsSlice };
        if (campaignId in state.collaborationDetails.collaborations &&
            !force &&
            isFresh(state.collaborationDetails.collaborations[campaignId].last_fetched_at, STALE_TIME) &&
            !state.collaborationDetails.collaborations[campaignId].expired
        ) {
            return rejectWithValue(ERROR.CACHED);
        }

        try {
            const collaborationData = await fetchCollaborationAPI(campaignId);
            return { collaborationData, campaignId };
        } catch (error) {
            return rejectWithValue(ERROR.NOT_FOUND);
        }

    }
)

export const fetchCollaborationChatRoom = createAsyncThunk(
    'collaborations/chat-room',
    async ({campaignId, force=false}: {campaignId: string, force?: boolean}, { getState, rejectWithValue }) => {
        const state = getState() as { collaborationDetails: CollaborationDetailsSlice };
        if (campaignId in state.collaborationDetails.chatRooms &&
            !force &&
            isFresh(state.collaborationDetails.chatRooms[campaignId].last_fetched_at, STALE_TIME)
        ) {
            return {room: state.collaborationDetails.chatRooms[campaignId].room, fresh: false, campaignId};
        }

        try {
            const room = await getCollabChatRoom(campaignId);
            return { room, campaignId, fresh: true };
        } catch (error) {
            return rejectWithValue(ERROR.NOT_FOUND);
        }

    }
)

const collaborationDetailsSlice = createSlice({
    name: 'collaborationDetails',
    initialState,
    reducers: {
        markExpired: (state, data) => {
            const campaignId = data.payload;
            if(campaignId && campaignId in state.collaborations){
                state.collaborations[campaignId] = {...state.collaborations[campaignId], expired: true};
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCollaboration.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.error_code = null
            })
            .addCase(fetchCollaboration.fulfilled, (state, action) => {
                state.collaborations[action.payload.campaignId] = { ...action.payload, last_fetched_at: Date.now(), expired: false }
                state.loading = false;
            })
            .addCase(fetchCollaboration.rejected, (state, action) => {
                state.loading = false;
                if (action.payload !== ERROR.CACHED) {
                    switch (action.payload) {
                        case ERROR.NOT_FOUND:
                        default:
                            state.error_code = errors[ERROR.NOT_FOUND].code;
                            state.error = errors[ERROR.NOT_FOUND].message;
                    }
                }
            })
            .addCase(fetchCollaborationChatRoom.fulfilled, (state, {payload}) => {
                const {campaignId, fresh, room} = payload;
                if(!fresh) return;
                state.chatRooms[campaignId] = {
                    room,
                    last_fetched_at: Date.now(),
                }
            })
    }
})

export const { markExpired } = collaborationDetailsSlice.actions;


export default collaborationDetailsSlice.reducer